import axios from 'axios';
import { buildApiUrl } from '../config/api';

export interface User {
  id: number;
  username: string;
  email: string;
  is_active: boolean;
  is_admin: boolean;
  created_at: string;
  updated_at: string;
}

export interface CreateUserRequest {
  username: string;
  email: string;
  password: string;
  is_admin?: boolean;
}

export interface UpdateUserRequest {
  username?: string;
  email?: string;
  is_admin?: boolean;
}

export interface ResetPasswordRequest {
  new_password: string;
}

export interface UserStats {
  total_users: number;
  active_users: number;
  admin_users: number;
  inactive_users: number;
}

class UserService {
  private baseURL = '/api/admin/users';

  // Get all users
  async getAllUsers(): Promise<User[]> {
    try {
      const response = await axios.get(buildApiUrl(this.baseURL));
      return response.data.data.users || [];
    } catch (error: any) {
      console.error('Failed to fetch users:', error);
      throw new Error(error.response?.data?.error || 'Failed to fetch users');
    }
  }

  // Create a new user
  async createUser(userData: CreateUserRequest): Promise<User> {
    try {
      const response = await axios.post(buildApiUrl(this.baseURL), userData);
      return response.data.data;
    } catch (error: any) {
      console.error('Failed to create user:', error);
      throw new Error(error.response?.data?.error || 'Failed to create user');
    }
  }

  // Update user
  async updateUser(userId: number, userData: UpdateUserRequest): Promise<User> {
    try {
      const response = await axios.put(buildApiUrl(`${this.baseURL}/${userId}`), userData);
      return response.data.data;
    } catch (error: any) {
      console.error('Failed to update user:', error);
      throw new Error(error.response?.data?.error || 'Failed to update user');
    }
  }

  // Delete user
  async deleteUser(userId: number): Promise<void> {
    try {
      await axios.delete(buildApiUrl(`${this.baseURL}/${userId}`));
    } catch (error: any) {
      console.error('Failed to delete user:', error);
      throw new Error(error.response?.data?.error || 'Failed to delete user');
    }
  }

  // Toggle user status (active/inactive)
  async toggleUserStatus(userId: number): Promise<User> {
    try {
      const response = await axios.put(buildApiUrl(`${this.baseURL}/${userId}/toggle-status`));
      return response.data.data;
    } catch (error: any) {
      console.error('Failed to toggle user status:', error);
      throw new Error(error.response?.data?.error || 'Failed to toggle user status');
    }
  }

  // Reset user password
  async resetUserPassword(userId: number, passwordData: ResetPasswordRequest): Promise<void> {
    try {
      await axios.put(buildApiUrl(`${this.baseURL}/${userId}/reset-password`), passwordData);
    } catch (error: any) {
      console.error('Failed to reset user password:', error);
      throw new Error(error.response?.data?.error || 'Failed to reset user password');
    }
  }

  // Get user statistics
  async getUserStats(): Promise<UserStats> {
    try {
      const users = await this.getAllUsers();
      const stats: UserStats = {
        total_users: users.length,
        active_users: users.filter(user => user.is_active).length,
        admin_users: users.filter(user => user.is_admin).length,
        inactive_users: users.filter(user => !user.is_active).length
      };
      return stats;
    } catch (error: any) {
      console.error('Failed to get user stats:', error);
      throw new Error(error.response?.data?.error || 'Failed to get user statistics');
    }
  }

  // Validate user data
  validateUserData(userData: CreateUserRequest | UpdateUserRequest): string[] {
    const errors: string[] = [];

    if ('username' in userData && userData.username) {
      if (userData.username.length < 3) {
        errors.push('Username must be at least 3 characters long');
      }
      if (!/^[a-zA-Z0-9_]+$/.test(userData.username)) {
        errors.push('Username can only contain letters, numbers, and underscores');
      }
    }

    if ('email' in userData && userData.email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(userData.email)) {
        errors.push('Please enter a valid email address');
      }
    }

    if ('password' in userData && userData.password) {
      if (userData.password.length < 6) {
        errors.push('Password must be at least 6 characters long');
      }
    }

    return errors;
  }

  // Format user for display
  formatUserForDisplay(user: User) {
    return {
      ...user,
      status: user.is_active ? 'Active' : 'Inactive',
      role: user.is_admin ? 'Admin' : 'User',
      created_at_formatted: new Date(user.created_at).toLocaleDateString(),
      updated_at_formatted: new Date(user.updated_at).toLocaleDateString()
    };
  }
}

export const userService = new UserService();
export default userService;
